import { z } from 'zod';

/**
 * Formulari de codis de personal de les portes JOAN (`plans/doors-payloads-from-pms.md`, fitxa P3).
 *
 * El servidor torna a validar exactament el mateix (fitxa P2, `POST /access-codes/joan`): nom d'1 a
 * 40 caràcters sense «/» ni «&» (viatgen crus dins l'URL que la placa demana), codi de 4 xifres i
 * abast «Totes / Només entrada / Triar». Aquí només s'hi guanya que l'error surti abans d'enviar.
 *
 * Proveïdors (portes v1, sprint 06 §4): el tipus `SUPPLIER` porta una franja diària (`dailyFrom` i
 * `dailyUntil`, `HH:mm` de Madrid, obligatòria i sense passar la mitjanit) i només va a l'entrada o a
 * portes triades, mai a «Totes»: el backend només l'admet a les entrades amb la placa nova.
 *
 * Les dates del formulari són de calendari (`yyyy-MM-dd`), com les tria en Pere al mòbil;
 * `buildJoanCodePayload` les converteix a instants (00:00 i 23:59:59 de la zona del navegador,
 * que a l'hotel és la de Madrid) just abans del `POST`.
 */

/** «Sense caducitat» = 31-DES-2035 (fitxa P3). Prou lluny perquè ningú hi pensi, prou a prop per no ser infinit. */
export const JOAN_CODE_NO_EXPIRY_DATE = '2035-12-31';

/** Els tipus que accepta el backend (`SUPPLIER`, proveïdor amb franja diària, des de les portes v1). */
export const JOAN_CODE_TYPES = ['STAFF', 'CLEANING', 'MASTER', 'SUPPLIER'] as const;
export type JoanCodeType = (typeof JOAN_CODE_TYPES)[number];

/** `ALL` = totes les portes · `ENTRANCE` = només l'entrada · `LOCKS` = les triades a `lockIds`. */
export const JOAN_CODE_SCOPES = ['ALL', 'ENTRANCE', 'LOCKS'] as const;
export type JoanCodeScope = (typeof JOAN_CODE_SCOPES)[number];

export const JOAN_CODE_TYPE_LABELS: Record<JoanCodeType, string> = {
  STAFF: 'Personal',
  CLEANING: 'Neteja',
  MASTER: 'Mestre',
  SUPPLIER: 'Proveïdor',
};

export const JOAN_CODE_SCOPE_LABELS: Record<JoanCodeScope, string> = {
  ALL: 'Totes',
  ENTRANCE: 'Només entrada',
  LOCKS: 'Triar',
};

/** Un proveïdor només va a l'entrada o a portes triades (entrades amb la placa nova), mai a «Totes». */
export const SUPPLIER_CODE_SCOPES = ['ENTRANCE', 'LOCKS'] as const satisfies readonly JoanCodeScope[];

export const SUPPLIER_CODE_SCOPE_LABELS: Record<(typeof SUPPLIER_CODE_SCOPES)[number], string> = {
  ENTRANCE: 'Entrada',
  LOCKS: 'Portes triades',
};

/** Els abasts que es poden triar amb aquest tipus, amb la seva etiqueta. */
export function scopeOptionsFor(type: JoanCodeType): Array<{ value: JoanCodeScope; label: string }> {
  if (type === 'SUPPLIER') {
    return SUPPLIER_CODE_SCOPES.map((value) => ({ value, label: SUPPLIER_CODE_SCOPE_LABELS[value] }));
  }
  return JOAN_CODE_SCOPES.map((value) => ({ value, label: JOAN_CODE_SCOPE_LABELS[value] }));
}

/** Hora `HH:mm` de 00:00 a 23:59 (la mateixa regla que `DAILY_TIME_PATTERN` del backend). */
export const DAILY_TIME_PATTERN = /^([01][0-9]|2[0-3]):[0-5][0-9]$/;

const calendarDate = (label: string) =>
  z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, `${label}: falta la data`);

export const joanCodeSchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(1, 'Escriu un nom')
      .max(40, 'Com a màxim 40 caràcters')
      .refine((value) => !/[/&]/.test(value), 'El nom no pot portar «/» ni «&»'),
    code: z.string().regex(/^[0-9]{4}$/, 'El codi són 4 xifres'),
    type: z.enum(JOAN_CODE_TYPES),
    scope: z.enum(JOAN_CODE_SCOPES),
    lockIds: z.array(z.string()),
    validFrom: calendarDate('Des de'),
    validUntil: calendarDate('Fins a'),
    /** Només amb `SUPPLIER`; buit amb els altres tipus. */
    dailyFrom: z.string(),
    dailyUntil: z.string(),
  })
  .refine((data) => data.scope !== 'LOCKS' || data.lockIds.length > 0, {
    message: 'Tria almenys una porta',
    path: ['lockIds'],
  })
  // Comparació de text: `yyyy-MM-dd` ja ordena bé.
  .refine((data) => data.validUntil >= data.validFrom, {
    message: 'La data final no pot ser anterior a la inicial',
    path: ['validUntil'],
  })
  .superRefine((data, ctx) => {
    if (data.type !== 'SUPPLIER') return;
    if (data.scope === 'ALL') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Un proveïdor només va a l’entrada o a portes triades',
        path: ['scope'],
      });
    }
    const fromOk = DAILY_TIME_PATTERN.test(data.dailyFrom);
    const untilOk = DAILY_TIME_PATTERN.test(data.dailyUntil);
    if (!fromOk) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Falta l’hora d’inici', path: ['dailyFrom'] });
    }
    if (!untilOk) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Falta l’hora final', path: ['dailyUntil'] });
    }
    // Comparació de text: `HH:mm` ja ordena bé. Cap franja passa la mitjanit (el backend tampoc).
    if (fromOk && untilOk && data.dailyFrom >= data.dailyUntil) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'L’hora final ha de ser després de la inicial',
        path: ['dailyUntil'],
      });
    }
  });

export type JoanCodeFormData = z.infer<typeof joanCodeSchema>;

/** Data d'avui en `yyyy-MM-dd`, en hora local (no UTC: a les 01:00 de Madrid encara és ahir a Greenwich). */
export function todayCalendarDate(now: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** `yyyy-MM-dd` + hora local → ISO. `end` posa el final del dia (23:59:59). */
export function calendarDateToIso(date: string, edge: 'start' | 'end'): string {
  const [year, month, day] = date.split('-').map(Number);
  const time = edge === 'start' ? [0, 0, 0, 0] : [23, 59, 59, 0];
  return new Date(year, month - 1, day, time[0], time[1], time[2], time[3]).toISOString();
}

/** Cos del `POST /access-codes/joan` (fitxa P2). */
export interface JoanCodePayload {
  name: string;
  code: string;
  type: JoanCodeType;
  scope: JoanCodeScope;
  /** Només quan l'abast és `LOCKS`: el backend el rebutja en els altres dos. */
  lockIds?: string[];
  validFrom: string;
  validUntil: string;
  /** Només amb `SUPPLIER`: el backend els rebutja amb els altres tipus. */
  dailyFrom?: string;
  dailyUntil?: string;
}

/** Cos del `POST /access-codes/joan` a partir del formulari. */
export function buildJoanCodePayload(data: JoanCodeFormData): JoanCodePayload {
  return {
    name: data.name.trim(),
    code: data.code,
    type: data.type,
    scope: data.scope,
    ...(data.scope === 'LOCKS' ? { lockIds: data.lockIds } : {}),
    validFrom: calendarDateToIso(data.validFrom, 'start'),
    validUntil: calendarDateToIso(data.validUntil, 'end'),
    ...(data.type === 'SUPPLIER' ? { dailyFrom: data.dailyFrom, dailyUntil: data.dailyUntil } : {}),
  };
}

/** Valors per començar: personal, totes les portes, vàlid d'avui fins que es digui el contrari. */
export function joanCodeDefaults(now: Date = new Date()): JoanCodeFormData {
  return {
    name: '',
    code: '',
    type: 'STAFF',
    scope: 'ALL',
    lockIds: [],
    validFrom: todayCalendarDate(now),
    validUntil: JOAN_CODE_NO_EXPIRY_DATE,
    dailyFrom: '',
    dailyUntil: '',
  };
}

/** Firmware de la placa nova (portes v1): l'únic on pot anar un proveïdor. */
export const FIRMWARE_V1 = 'esphome-v1';

/** El firmware d'un pany de `/locks` (`providerData.firmware`); sense, la placa vella. */
export function lockFirmware(lock: { providerData?: unknown }): string {
  let data: unknown = lock.providerData;
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data);
    } catch {
      data = null;
    }
  }
  const firmware = (data as { firmware?: unknown } | null)?.firmware;
  return typeof firmware === 'string' && firmware.length > 0 ? firmware : 'joan-nodemcu';
}

/** Les portes que pot portar un codi d'aquest tipus. Un proveïdor: entrades amb la placa nova. */
export function locksForType<T extends { type: string; providerData?: unknown }>(
  locks: T[],
  type: JoanCodeType,
): T[] {
  if (type !== 'SUPPLIER') return locks;
  return locks.filter((lock) => lock.type === 'ENTRANCE' && lockFirmware(lock) === FIRMWARE_V1);
}

/** Avís quan encara no hi ha cap entrada amb la placa nova (avui: l'A1 encara és JOAN). */
export const NO_V1_ENTRANCE_TEXT =
  'Encara no hi ha cap entrada amb la placa nova: un proveïdor no hi pot anar fins que l’entrada passi a la v1.';

/**
 * El missatge del 400 del backend quan l'abast no té cap entrada amb la placa nova, dit clar. La
 * resta de missatges passen tal qual.
 */
export function supplierApiMessage(message: string): string {
  return /^Cap entrada de l'abast te la placa nova/.test(message) ? NO_V1_ENTRANCE_TEXT : message;
}
